import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { documentApi, documentKeys } from "@/features/documents/api";

import { chatApi, chatKeys } from "./api";

/**
 * Delivery is by polling, not a socket.
 *
 * Forty people sending each other a few lines a day do not need a second
 * protocol and a second process to keep alive. An open thread asks every five
 * seconds; the unread badge asks every half minute, and only while the tab is
 * in front of somebody.
 */
const OPEN_THREAD_MS = 5_000;
const BADGE_MS = 30_000;

export function useConversations() {
  return useQuery({
    queryKey: chatKeys.conversations(),
    queryFn: chatApi.conversations,
    refetchInterval: BADGE_MS,
  });
}

export function useUnreadMessages() {
  return useQuery({
    queryKey: chatKeys.unread(),
    queryFn: chatApi.unread,
    refetchInterval: BADGE_MS,
  });
}

export function useMessages(conversationId: string | null) {
  return useQuery({
    queryKey: chatKeys.messages(conversationId ?? "none"),
    queryFn: () => chatApi.messages(conversationId!),
    enabled: Boolean(conversationId),
    refetchInterval: conversationId ? OPEN_THREAD_MS : false,
  });
}

export function usePeopleToWriteTo(search: string) {
  return useQuery({
    queryKey: chatKeys.people(search),
    queryFn: () => chatApi.people(search),
    staleTime: 60_000,
  });
}

export function useWritableGroups() {
  return useQuery({
    queryKey: chatKeys.groups(),
    queryFn: chatApi.groups,
    staleTime: 5 * 60_000,
  });
}

export function useOpenGroup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: chatApi.openGroup,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: chatKeys.conversations() });
    },
  });
}

/** HR makes a chat group by hand. The groups list changes too. */
export function useCreateChatGroup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: { name: string; userIds: string[] }) =>
      chatApi.createGroup(args.name, args.userIds),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: chatKeys.all });
      void queryClient.invalidateQueries({ queryKey: ["groups"] });
    },
  });
}

export function useSendMessage(conversationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: string) => chatApi.send(conversationId, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: chatKeys.messages(conversationId) });
      void queryClient.invalidateQueries({ queryKey: chatKeys.conversations() });
    },
  });
}

export function useOpenDirect() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: chatApi.openDirect,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: chatKeys.conversations() });
    },
  });
}

export function useWithdrawMessage(conversationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: chatApi.withdraw,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: chatKeys.messages(conversationId) });
      void queryClient.invalidateQueries({ queryKey: chatKeys.conversations() });
    },
  });
}

/** One group, with how many people and messages it holds. HR only. */
export function useGroupDetail(groupId: string | null) {
  return useQuery({
    queryKey: [...documentKeys.groups(), "detail", groupId ?? ""],
    queryFn: () => documentApi.group(groupId!),
    enabled: Boolean(groupId),
  });
}

/**
 * HR deletes a group it made, and its chat goes with it.
 *
 * Every chat query is refreshed, not just the list: the thread somebody has
 * open, the unread badge and the groups they may write to all change.
 */
export function useDeleteChatGroup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (groupId: string) => documentApi.deleteGroup(groupId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: chatKeys.all });
      void queryClient.invalidateQueries({ queryKey: documentKeys.groups() });
    },
  });
}
